import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormHelperText,
  FormControlLabel,
  IconButton,
  Grid,
  MenuItem,
  Tooltip,
  TextField,
  Stack,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';

import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';

const createColumnValueRow = (value = '', keepExisting = false, id = null) => ({
  id: id || `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
  value,
  keepExisting
});

const normalizeColumnValueRows = (valueRows) => {
  if (valueRows === null || valueRows === undefined) {
    return [];
  }

  if (typeof valueRows === 'string') {
    if (!valueRows) {
      return [];
    }
    return valueRows.split(/\r?\n/).map((line) => createColumnValueRow(line, false));
  }

  if (!Array.isArray(valueRows)) {
    return [];
  }

  return valueRows.map((row) => {
    if (typeof row === 'string') {
      return createColumnValueRow(row, false);
    }

    if (row && typeof row === 'object') {
      return createColumnValueRow(
        row.value ?? row.text ?? row.columnValue ?? '',
        Boolean(row.keepExisting ?? row.keep_existing ?? row.keepCurrent),
        row.id ?? null
      );
    }

    return createColumnValueRow(String(row ?? ''), false);
  });
};

const serializeColumnValueRows = (rows) => rows.map((row) => ({
  value: row.keepExisting ? '' : String(row.value ?? ''),
  keepExisting: Boolean(row.keepExisting)
}));

const serializeColumnValueRowsToText = (rows) => rows
  .map((row) => (row.keepExisting ? '' : String(row.value ?? '')))
  .join('\n');

const SectionTitle = ({ label, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
    <Typography component="span" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500, lineHeight: 1.2 }}>
      {label}
    </Typography>
    {tooltip ? (
      <Tooltip title={tooltip} arrow>
        <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
      </Tooltip>
    ) : null}
  </Box>
);

const ActionsAddExcelColumn = ({ caseid, tags, settingId, onSaveStatusChange, initialScope = 'event' }) => {
  const { t } = useTranslation();

  const [sourcePath, setSourcePath] = useState('');
  const [targetPath, setTargetPath] = useState('/{filename}_with_column.xlsx');
  const [sheetName, setSheetName] = useState('');
  const [columnName, setColumnName] = useState('');
  const [columnValueRows, setColumnValueRows] = useState([]);
  const [insertAtColumn, setInsertAtColumn] = useState('');
  const [valueRow, setValueRow] = useState('2');
  const [startColumn, setStartColumn] = useState('1');
  const [searchRow, setSearchRow] = useState('1');
  const [searchMode, setSearchMode] = useState('contains');
  const [addIfNotFound, setAddIfNotFound] = useState(true);
  const [addIfNotFoundMode, setAddIfNotFoundMode] = useState('append_end');
  const [sourceBrowsingDisabled, setSourceBrowsingDisabled] = useState(false);
  const [targetBrowsingDisabled, setTargetBrowsingDisabled] = useState(false);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'addexcelcolumn',
    onSaveStatusChange,
    initialScope,
    customComparison: (initial = {}) => (
      sourcePath === (initial.sourceFilePath || '') &&
      targetPath === (initial.target || initial.targetPath || '') &&
      sheetName === (initial.sheetName || '') &&
      columnName === (initial.columnName || '') &&
      insertAtColumn === String(initial.insertAtColumn || initial.columnIndex || '') &&
      valueRow === String(initial.valueRow || initial.valueRowIndex || initial.rowIndex || '2') &&
      startColumn === String(initial.startColumn || initial.valueColumnIndex || initial.x || '1') &&
      searchRow === String(initial.searchRow || initial.searchRowIndex || initial.matchRow || '1') &&
      searchMode === String(initial.searchMode || initial.matchMode || 'contains') &&
      addIfNotFound === (initial.addIfNotFound !== undefined ? Boolean(initial.addIfNotFound) : true) &&
      addIfNotFoundMode === String(initial.addIfNotFoundMode || initial.addIfNotFoundPosition || 'append_end') &&
      JSON.stringify(serializeColumnValueRows(columnValueRows)) === JSON.stringify(serializeColumnValueRows(normalizeColumnValueRows(initial.columnValueRows || initial.columnValue || '')))
    ),
    customSaveData: () => ({
      sourceFilePath: sourcePath,
      target: targetPath,
      sheetName,
      columnName,
      insertAtColumn: insertAtColumn ? Number(insertAtColumn) : null,
      valueRow: valueRow ? Number(valueRow) : null,
      startColumn: startColumn ? Number(startColumn) : null,
      searchRow: searchRow ? String(searchRow) : null,
      searchMode,
      addIfNotFound,
      addIfNotFoundMode: addIfNotFound ? addIfNotFoundMode : 'append_end',
      columnValueRows: serializeColumnValueRows(columnValueRows),
      columnValue: serializeColumnValueRowsToText(columnValueRows)
    }),
    loadDataFunction: (data) => {
      const customData = data.customData || {};
      setSourcePath(customData.sourceFilePath || customData.sourcePath || '');
      setTargetPath(customData.target || customData.targetPath || '/{filename}_with_column.xlsx');
      setSheetName(customData.sheetName || '');
      setColumnName(customData.columnName || '');
      setInsertAtColumn(customData.insertAtColumn ? String(customData.insertAtColumn) : '');
      setValueRow(customData.valueRow || customData.valueRowIndex ? String(customData.valueRow || customData.valueRowIndex) : '2');
      setStartColumn(customData.startColumn || customData.valueColumnIndex || customData.x ? String(customData.startColumn || customData.valueColumnIndex || customData.x) : '1');
      setSearchRow(customData.searchRow || customData.searchRowIndex || customData.matchRow ? String(customData.searchRow || customData.searchRowIndex || customData.matchRow) : '1');
      setSearchMode((customData.searchMode || customData.matchMode || 'contains').toString());
      setAddIfNotFound(customData.addIfNotFound !== undefined ? Boolean(customData.addIfNotFound) : true);
      setAddIfNotFoundMode((customData.addIfNotFoundMode || customData.addIfNotFoundPosition || 'append_end').toString());
      setColumnValueRows(normalizeColumnValueRows(customData.columnValueRows || customData.columnValue || ''));
    }
  });

  const { handleBrowseDirectory: handleBrowseSourceFile, FolderBrowserComponent: SourceFileBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (filePath) => {
      if (filePath) {
        setSourcePath(filePath);
      }
      setSourceBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowsingDisabled(false);
    }
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        const folderPathClean = folderPath.replace(/[\\/]+$/, '');
        const separator = folderPathClean.includes('\\') ? '\\' : '/';
        setTargetPath(`${folderPathClean}${separator}{filename}_with_column.xlsx`);
      }
      setTargetBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setTargetBrowsingDisabled(false);
    }
  });

  useEffect(() => {
    updateSaveDisabledState();
  }, [sourcePath, targetPath, sheetName, columnName, columnValueRows, insertAtColumn, valueRow, startColumn, searchRow, searchMode, addIfNotFound, addIfNotFoundMode, updateSaveDisabledState]);

  const validation = useMemo(() => {
    if (!targetPath.trim()) {
      return {
        error: true,
        helper: t('workflow.add_excel_column.target_required', 'Choose a save path.')
      };
    }
    return { error: false, helper: '' };
  }, [targetPath, t]);

  const addColumnValueRow = () => {
    setColumnValueRows((currentRows) => [...currentRows, createColumnValueRow()]);
  };

  const updateColumnValueRow = (rowId, updater) => {
    setColumnValueRows((currentRows) => currentRows.map((row) => {
      if (row.id !== rowId) {
        return row;
      }
      const nextRow = typeof updater === 'function' ? updater(row) : { ...row, ...updater };
      return {
        ...row,
        ...nextRow
      };
    }));
  };

  const removeColumnValueRow = (rowId) => {
    setColumnValueRows((currentRows) => currentRows.filter((row) => row.id !== rowId));
  };

  const moveColumnValueRow = (rowId, direction) => {
    setColumnValueRows((currentRows) => {
      const index = currentRows.findIndex((row) => row.id === rowId);
      if (index < 0) {
        return currentRows;
      }

      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= currentRows.length) {
        return currentRows;
      }

      const nextRows = [...currentRows];
      const [movedRow] = nextRows.splice(index, 1);
      nextRows.splice(nextIndex, 0, movedRow);
      return nextRows;
    });
  };

  const customDataTags = [
    {
      key: 'filename',
      label: t('filename'),
      value: 'filename',
      description: t('workflow.add_excel_column.filename_description', 'File name without extension')
    },
    {
      key: 'file_extension',
      label: t('extension'),
      value: 'file_extension',
      description: t('workflow.add_excel_column.extension_description', 'File type without the dot')
    },
    {
      key: 'file_folder_path',
      label: t('file_folder_path'),
      value: 'file_folder_path',
      description: t('workflow.add_excel_column.file_folder_path_description', 'Folder path of the source file')
    }
  ];
  const checkedRowNumber = Math.max(1, Number.parseInt(searchRow, 10) || 1);

  return (
    <ActionSettingsLayout
      title={t('addexcelcolumn', 'Add column')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled || validation.error}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      customDataTags={customDataTags}
    >
      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={(
              <SectionTitle
                label={t('workflow.add_excel_column.file_section_title', 'Files and sheet')}
                tooltip={t('workflow.add_excel_column.file_section_tooltip', 'Pick the source file, where to save the result, and the sheet name.')}
              />
            )}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t(
                'workflow.add_excel_column.file_section_description',
                'Choose the file to read, the file to save, and the sheet to use.'
              )}
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ width: { xs: 74, sm: 110 }, flexShrink: 0, lineHeight: 1.1 }}
                >
                  {t('workflow.add_excel_column.source_label', 'File to read')}
                </Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <TagEditor
                    caseid={caseid}
                    value={sourcePath}
                    onChange={setSourcePath}
                    placeholder={t('workflow.add_excel_column.source_placeholder', 'Leave empty to use the current file')}
                    singleLine
                    tags={tags}
                  />
                </Box>
                <Tooltip title={t('workflow.add_excel_column.choose_file_button', 'Choose file')} arrow>
                  <span>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSourceBrowsingDisabled(true);
                        handleBrowseSourceFile();
                      }}
                      disabled={sourceBrowsingDisabled}
                      size="small"
                      sx={{ px: 1.25, minWidth: 'auto', whiteSpace: 'nowrap' }}
                      aria-label={t('workflow.add_excel_column.choose_file_button', 'Choose file')}
                    >
                      <FolderOpenOutlinedIcon sx={{ fontSize: '1rem' }} />
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ width: { xs: 74, sm: 110 }, flexShrink: 0, lineHeight: 1.1 }}
                >
                  {t('workflow.add_excel_column.target_label', 'Save to')}
                </Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <TagEditor
                    caseid={caseid}
                    value={targetPath}
                    onChange={setTargetPath}
                    placeholder={t('workflow.add_excel_column.target_placeholder', '/{filename}_with_column.xlsx')}
                    singleLine
                    tags={tags}
                  />
                </Box>
                <Tooltip title={t('workflow.add_excel_column.choose_folder_button', 'Folder')} arrow>
                  <span>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setTargetBrowsingDisabled(true);
                        handleBrowseTargetDirectory();
                      }}
                      disabled={targetBrowsingDisabled}
                      size="small"
                      sx={{ px: 1.25, minWidth: 'auto', whiteSpace: 'nowrap' }}
                      aria-label={t('workflow.add_excel_column.choose_folder_button', 'Folder')}
                    >
                      <FolderIcon sx={{ fontSize: '1rem' }} />
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ width: { xs: 74, sm: 110 }, flexShrink: 0, lineHeight: 1.1 }}
                >
                  {t('workflow.add_excel_column.sheet_label', 'Sheet')}
                </Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <TagEditor
                    caseid={caseid}
                    value={sheetName}
                    onChange={setSheetName}
                    placeholder={t('workflow.add_excel_column.sheet_placeholder', 'Sheet1')}
                    singleLine
                    tags={tags}
                  />
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={(
              <SectionTitle
                label={t('workflow.add_excel_column.match_title', 'Choose the row')}
                tooltip={t('workflow.add_excel_column.match_title_tooltip', 'We compare the column name you enter with the cells in this row.')}
              />
            )}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  {t('workflow.add_excel_column.column_name_label', 'Name to find')}
                </Typography>
                <TagEditor
                  caseid={caseid}
                  value={columnName}
                  onChange={setColumnName}
                  placeholder={t('workflow.add_excel_column.column_name_placeholder', 'status')}
                  singleLine
                  dense
                  minHeight="32px"
                  maxRows={1}
                  tags={tags}
                />
                <FormHelperText>
                  {t('workflow.add_excel_column.column_name_helper', 'We compare this name with the cells in the row you choose below.')}
                </FormHelperText>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  {t('workflow.add_excel_column.search_row_label', 'Row to check')}
                </Typography>
                <TagEditor
                  caseid={caseid}
                  value={searchRow}
                  onChange={setSearchRow}
                  placeholder={t('workflow.add_excel_column.search_row_placeholder', '1')}
                  singleLine
                  dense
                  minHeight="32px"
                  maxRows={1}
                  tags={tags}
                />
                <FormHelperText>
                  {t('workflow.add_excel_column.search_row_helper', 'We compare the column name you enter with the cells in this row.')}
                </FormHelperText>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  {t('workflow.add_excel_column.start_column_label', 'Starting column')}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={startColumn}
                  onChange={(event) => setStartColumn(event.target.value)}
                  placeholder={t('workflow.add_excel_column.start_column_placeholder', '1')}
                  inputProps={{ min: 1 }}
                />
                <FormHelperText>
                  {t(
                    'workflow.add_excel_column.start_column_helper',
                    'Search starts from this column. When the column is missing and the mode inserts by order, the new column is inserted here.'
                  )}
                </FormHelperText>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  {t('workflow.add_excel_column.search_mode_label', 'How to match')}
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={searchMode}
                  onChange={(event) => setSearchMode(event.target.value)}
                  helperText={t('workflow.add_excel_column.search_mode_helper', 'Contains means the name can be inside a cell. Exact match means the cell must match it exactly.')}
                >
                  <MenuItem value="contains">
                    {t('workflow.add_excel_column.search_mode_contains', 'Contains')}
                  </MenuItem>
                  <MenuItem value="exact">
                    {t('workflow.add_excel_column.search_mode_exact', 'Exact match')}
                  </MenuItem>
                  <MenuItem value="starts_with">
                    {t('workflow.add_excel_column.search_mode_starts_with', 'Starts with')}
                  </MenuItem>
                </TextField>
              </Box>
              <Box>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={addIfNotFound}
                      onChange={(event) => setAddIfNotFound(event.target.checked)}
                      color="primary"
                      size="small"
                    />
                  )}
                  label={t(
                    'workflow.add_excel_column.add_new_column_if_not_found_label',
                    'Add new column if not found'
                  )}
                />
                {addIfNotFound ? (
                  <Box sx={{ pl: 4.2, pr: 0.5, mt: 0.5 }}>
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('workflow.add_excel_column.add_new_column_mode_label', 'When not found')}
                    </Typography>
                    <TextField
                      fullWidth
                      select
                      value={addIfNotFoundMode}
                      onChange={(event) => setAddIfNotFoundMode(event.target.value)}
                      helperText={t(
                        'workflow.add_excel_column.add_new_column_mode_helper',
                        'Choose whether the missing column is appended at the end or inserted at the beginning of the sheet.'
                      )}
                    >
                      <MenuItem value="append_end">
                        {t('workflow.add_excel_column.add_new_column_mode_append_end', 'Add at the end')}
                      </MenuItem>
                      <MenuItem value="insert_by_order">
                        {t(
                          'workflow.add_excel_column.add_new_column_mode_insert_by_order',
                          'Add by order starting from the first element'
                        )}
                      </MenuItem>
                    </TextField>
                  </Box>
                ) : null}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={(
              <SectionTitle
                label={t('workflow.add_excel_column.column_title', 'Values to add')}
                tooltip={t('workflow.add_excel_column.column_title_tooltip', 'These are the values that will be written in the new column.')}
              />
            )}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  {t('workflow.add_excel_column.column_value_label', 'Values')}
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: '#fff'
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '64px minmax(0, 1fr) 180px',
                      alignItems: 'center',
                      backgroundColor: '#f3f7f3',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    <Box sx={{ px: 1.25, py: 0.75, borderRight: '1px solid', borderColor: 'divider' }}>
                      #
                    </Box>
                    <Box sx={{ px: 1.25, py: 0.75, borderRight: '1px solid', borderColor: 'divider' }}>
                      A
                    </Box>
                    <Box sx={{ px: 1.25, py: 0.75 }}>
                      {t('workflow.add_excel_column.actions_label', 'Actions')}
                    </Box>
                  </Box>
                  <Stack spacing={0}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '56px minmax(0, 1fr) 156px',
                      alignItems: 'center',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      minHeight: 44,
                      backgroundColor: '#eef4ff'
                    }}
                  >
                    <Box
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        fontWeight: 700
                      }}
                    >
                      {checkedRowNumber}
                    </Box>
                    <Box
                      sx={{
                        px: 1.1,
                        py: 0.7,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0
                      }}
                    >
                      <TagEditor
                        caseid={caseid}
                        value={columnName}
                        onChange={setColumnName}
                        placeholder={t('workflow.add_excel_column.column_name_placeholder', 'status')}
                        singleLine
                        dense
                        minHeight="32px"
                        maxRows={1}
                        tags={tags}
                        disabled
                      />
                    </Box>
                    <Box
                      sx={{
                        px: 0.75,
                        py: 0.35,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        flexWrap: 'nowrap',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <LockOutlinedIcon fontSize="small" color="primary" />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {t('workflow.add_excel_column.search_row_preview_label', 'Search row')}
                      </Typography>
                    </Box>
                  </Box>
                  {columnValueRows.length === 0 ? (
                    <Box sx={{
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      color: 'text.secondary'
                    }}>
                      {t('workflow.add_excel_column.column_values_empty', 'No values yet. Add one below.')}
                    </Box>
                  ) : (
                    columnValueRows.map((row, index) => {
                      const targetRowNumber = checkedRowNumber + index + 1;

                      return (
                        <Box
                          key={row.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '56px minmax(0, 1fr) 156px',
                            alignItems: 'center',
                            borderBottom: index === columnValueRows.length - 1 ? 'none' : '1px solid',
                            borderColor: 'divider',
                            minHeight: 44,
                            '&:hover': {
                              backgroundColor: '#fbfdfb'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRight: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f8faf8',
                              color: 'text.secondary',
                              fontWeight: 700
                            }}
                          >
                            {targetRowNumber}
                          </Box>
                          <Box
                            sx={{
                              px: 1.1,
                              py: 0.7,
                              borderRight: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              minWidth: 0,
                              width: '100%'
                            }}
                          >
                            {!row.keepExisting ? (
                              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                <TagEditor
                                  caseid={caseid}
                                  value={row.value}
                                  onChange={(nextValue) => updateColumnValueRow(row.id, { value: nextValue })}
                                  placeholder={t('workflow.add_excel_column.column_value_row_placeholder', 'Type a value')}
                                  singleLine
                                  dense
                                  minHeight="32px"
                                  maxRows={1}
                                  tags={tags}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  color: 'text.secondary',
                                  fontStyle: 'italic',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {t('workflow.add_excel_column.keep_existing_label', 'Keep current value')}
                              </Box>
                            )}
                          </Box>
                          <Box
                            sx={{
                              px: 0.75,
                              py: 0.35,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 0.5,
                              flexWrap: 'nowrap',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Tooltip
                              title={row.keepExisting
                                ? t('workflow.add_excel_column.keep_existing_enabled', 'Keep current cell value')
                                : t('workflow.add_excel_column.keep_existing_disabled', 'Write a new value')}
                              arrow
                            >
                              <IconButton
                                size="small"
                                onClick={() => updateColumnValueRow(row.id, { keepExisting: !row.keepExisting })}
                                sx={{ p: 0.15, flexShrink: 0 }}
                                aria-label={row.keepExisting
                                  ? t('workflow.add_excel_column.keep_existing_enabled', 'Keep current cell value')
                                  : t('workflow.add_excel_column.keep_existing_disabled', 'Write a new value')}
                              >
                                {row.keepExisting ? (
                                  <LockOutlinedIcon fontSize="inherit" color="primary" />
                                ) : (
                                  <EditOutlinedIcon fontSize="inherit" color="action" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, flexShrink: 0 }}>
                              <Tooltip title={t('workflow.add_excel_column.move_row_up', 'Move up')} arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveColumnValueRow(row.id, -1)}
                                    disabled={index === 0}
                                    sx={{ p: 0.15 }}
                                  >
                                    <ArrowUpwardIcon fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={t('workflow.add_excel_column.move_row_down', 'Move down')} arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveColumnValueRow(row.id, 1)}
                                    disabled={index === columnValueRows.length - 1}
                                    sx={{ p: 0.15 }}
                                  >
                                    <ArrowDownwardIcon fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={t('workflow.add_excel_column.remove_row', 'Remove row')} arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeColumnValueRow(row.id)}
                                    sx={{ p: 0.15 }}
                                  >
                                    <DeleteOutlineIcon fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                  </Stack>
                </Box>
                <Button variant="outlined" onClick={addColumnValueRow} startIcon={<AddIcon />}>
                  {t('workflow.add_excel_column.add_row_button', 'Add value')}
                </Button>
                <FormHelperText error={validation.error}>
                  {validation.error ? validation.helper : t(
                    'workflow.add_excel_column.column_helper',
                    'Add one value per line. Use the arrows to reorder. Keep the checkbox on when you want to leave the current value as is.'
                  )}
                </FormHelperText>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {SourceFileBrowserComponent}
      {TargetFolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default ActionsAddExcelColumn;
